export interface Coordinates {
  latitude: number
  longitude: number
  accuracy?: number
}

export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000 // Earth radius in meters
  const lat1Rad = (lat1 * Math.PI) / 180
  const lat2Rad = (lat2 * Math.PI) / 180
  const deltaLat = ((lat2 - lat1) * Math.PI) / 180
  const deltaLon = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

let cachedPosition: { coords: Coordinates; timestamp: number } | null = null
const LAST_KNOWN_KEY = "last-known-location"

function readLastKnownLocation(): Coordinates | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(LAST_KNOWN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { latitude: number; longitude: number }
    if (typeof parsed.latitude === "number" && typeof parsed.longitude === "number") {
      return { latitude: parsed.latitude, longitude: parsed.longitude }
    }
  } catch {
    return null
  }
  return null
}

function writeLastKnownLocation(coords: Coordinates): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(LAST_KNOWN_KEY, JSON.stringify(coords))
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

export async function getCurrentLocation(): Promise<Coordinates> {
  console.log("[v0] [GPS] Requesting current location...")

  // Check if geolocation is available
  if (!navigator.geolocation) {
    console.error("[v0] [GPS] Geolocation not supported by browser")
    throw new Error("Geolocation not supported")
  }

  if (cachedPosition && Date.now() - cachedPosition.timestamp < 60000) {
    console.log("[v0] [GPS] Using cached position:", cachedPosition.coords)
    return cachedPosition.coords
  }

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true, // Force high accuracy GPS
        timeout: 15000,
        maximumAge: 10000, // Reduced from 60000 to get fresher position
      })
    })

    const coords = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    }

    console.log("[v0] [GPS] New position acquired:", coords)
    console.log("[v0] [GPS] Accuracy:", position.coords.accuracy, "meters")

    cachedPosition = { coords, timestamp: Date.now() }
    writeLastKnownLocation(coords)
    return coords
  } catch (err: any) {
    console.error("[v0] [GPS] Error getting position:", err.message, err.code)
    if (cachedPosition) {
      console.warn("[v0] [GPS] Using cached position due to error")
      return cachedPosition.coords
    }
    const lastKnown = readLastKnownLocation()
    if (lastKnown) {
      console.warn("[v0] [GPS] Using last known location from storage due to error")
      return lastKnown
    }
    throw err
  }
}

export function formatRange(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)}km`
  }
  return `${meters}m`
}
